import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import { useTranslation } from 'react-i18next';
import { FirebaseContext } from 'common';
import RichTextEditor from './RichTextEditor.jsx';

const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const normalizeCategory = (category) => {
  const safe = category && typeof category === 'object' ? category : {};
  const translations = safe.translations && typeof safe.translations === 'object' ? safe.translations : {};
  const featured = Boolean(safe.featured);
  const published = safe.published !== false;

  return {
    id: safe.id || '',
    title: safe.title || '',
    description: safe.description || '',
    parentId: safe.parentId || '',
    image: safe.image || '',
    featured,
    published,
    seo: {
      title: safe.seo?.title || '',
      description: safe.seo?.description || '',
      image: safe.seo?.image || '',
    },
    translations,
  };
};

const pruneTranslations = (translations) => {
  if (!translations || typeof translations !== 'object') return undefined;
  const out = {};
  Object.entries(translations).forEach(([locale, value]) => {
    if (!value || typeof value !== 'object') return;
    const cleaned = {};
    if (value.title) cleaned.title = value.title;
    if (value.description) cleaned.description = value.description;
    if (value.seo && typeof value.seo === 'object') {
      const seo = {};
      if (value.seo.title) seo.title = value.seo.title;
      if (value.seo.description) seo.description = value.seo.description;
      if (Object.keys(seo).length) cleaned.seo = seo;
    }
    if (Object.keys(cleaned).length) out[locale] = cleaned;
  });
  return Object.keys(out).length ? out : undefined;
};

const ImageUrlField = ({
  label,
  value,
  onChange,
  helperText,
  disabled,
  uploadPathPrefix = 'website_assets/storefront/shop/categories',
  previewSize = 64,
}) => {
  const firebase = useContext(FirebaseContext);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [failed, setFailed] = useState(false);

  const hasValue = Boolean(value);
  const canUpload = Boolean(firebase?.storage) && !disabled;

  useEffect(() => {
    setFailed(false);
  }, [value]);

  const handleUploadClick = () => {
    if (!canUpload) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!firebase?.storage) {
      alert('Storage is not available yet.');
      return;
    }

    setUploading(true);
    try {
      const safeName = String(file.name || 'image')
        .replace(/[^a-z0-9._-]+/gi, '_')
        .slice(0, 80);
      const path = `${uploadPathPrefix}/${Date.now()}_${safeName}`;
      const ref = firebase.storage.ref(path);
      await ref.put(file);
      const url = await ref.getDownloadURL();
      onChange(url);
    } catch (error) {
      console.error('Image upload failed:', error);
      alert('Image upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleOpen = () => {
    if (!hasValue) return;
    window.open(value, '_blank', 'noopener,noreferrer');
  };

  const handleClear = () => onChange('');

  return (
    <Stack spacing={1}>
      <TextField
        fullWidth
        label={label}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        helperText={helperText}
        disabled={disabled}
      />

      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            startIcon={<CloudUploadRoundedIcon />}
            onClick={handleUploadClick}
            disabled={!canUpload || uploading}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewRoundedIcon />}
            onClick={handleOpen}
            disabled={!hasValue}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}
          >
            Open
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<LinkOffRoundedIcon />}
            onClick={handleClear}
            disabled={!hasValue || disabled}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}
          >
            Clear
          </Button>
        </Stack>

        <Box
          sx={{
            width: previewSize,
            height: previewSize,
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
            bgcolor: '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {hasValue && !failed ? (
            <Box
              component="img"
              alt=""
              src={value}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={() => setFailed(true)}
            />
          ) : (
            <ImageRoundedIcon sx={{ color: '#94a3b8' }} />
          )}
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelected}
          style={{ display: 'none' }}
        />
      </Stack>
    </Stack>
  );
};

const SectionNav = ({ sections, onJump }) => (
  <List dense disablePadding sx={{ position: 'sticky', top: 24 }}>
    {sections.map((section) => (
      <ListItemButton key={section.key} onClick={() => onJump(section.key)} sx={{ borderRadius: 2, mb: 0.5 }}>
        <ListItemText
          primary={section.label}
          secondary={section.description}
          primaryTypographyProps={{ fontWeight: 900, fontSize: 13 }}
          secondaryTypographyProps={{ fontSize: 12 }}
        />
      </ListItemButton>
    ))}
  </List>
);

export default function ShopCategoryEditorDialog({
  open,
  onClose,
  value,
  onSave,
  categories = [],
  languages = [],
  uploadPathPrefix = 'website_assets/storefront/shop/categories',
}) {
  const { t } = useTranslation();

  const languageTabs = useMemo(() => {
    const list = Array.isArray(languages) ? languages : [];
    const codes = list
      .map((lang) => ({ code: lang?.langLocale || lang?.id, label: lang?.langName }))
      .filter((item) => item.code);
    return [{ code: 'base', label: t('default') || 'Default' }, ...codes];
  }, [languages, t]);

  const [activeLocale, setActiveLocale] = useState('base');
  const [draft, setDraft] = useState(() => normalizeCategory(value));
  const [expanded, setExpanded] = useState({
    basics: true,
    media: true,
    seo: false,
    publishing: true,
  });
  const [parentInput, setParentInput] = useState('');

  useEffect(() => {
    if (!open) return;
    const normalized = normalizeCategory(value);
    setDraft(normalized);
    setActiveLocale('base');
    setExpanded({ basics: true, media: true, seo: false, publishing: true });
    setParentInput(normalized.parentId || '');
  }, [open, value]);

  const sectionRefs = {
    basics: useRef(null),
    media: useRef(null),
    seo: useRef(null),
    publishing: useRef(null),
  };

  const sections = useMemo(
    () => [
      { key: 'basics', label: t('basics') || 'Basics', description: 'Title, description, hierarchy.' },
      { key: 'media', label: t('media') || 'Media', description: 'Category image.' },
      { key: 'seo', label: t('seo') || 'SEO', description: 'Meta title, description, share image.' },
      { key: 'publishing', label: t('publishing') || 'Publishing', description: 'Visibility.' },
    ],
    [t]
  );

  const parentOptions = useMemo(() => {
    const list = Array.isArray(categories) ? categories : [];
    return list
      .map((cat) => normalizeCategory(cat))
      .filter((cat) => cat.id && cat.id !== draft.id)
      .sort((a, b) => String(a.title || a.id).localeCompare(String(b.title || b.id)));
  }, [categories, draft.id]);

  const handleJump = (key) => {
    const node = sectionRefs[key]?.current;
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const getLocalizedValue = (field) => {
    if (activeLocale === 'base') return draft[field] || '';
    const trans = draft.translations?.[activeLocale] || {};
    return trans[field] || '';
  };

  const setLocalizedValue = (field, valueToSet) => {
    if (activeLocale === 'base') {
      setDraft((prev) => ({ ...prev, [field]: valueToSet }));
      return;
    }
    setDraft((prev) => {
      const existing = prev.translations?.[activeLocale] || {};
      return {
        ...prev,
        translations: {
          ...(prev.translations || {}),
          [activeLocale]: {
            ...existing,
            [field]: valueToSet,
          },
        },
      };
    });
  };

  const getLocalizedSeoValue = (field) => {
    if (activeLocale === 'base') return draft.seo?.[field] || '';
    const trans = draft.translations?.[activeLocale] || {};
    return trans.seo?.[field] || '';
  };

  const setLocalizedSeoValue = (field, valueToSet) => {
    if (activeLocale === 'base') {
      setDraft((prev) => ({ ...prev, seo: { ...(prev.seo || {}), [field]: valueToSet } }));
      return;
    }
    setDraft((prev) => {
      const existing = prev.translations?.[activeLocale] || {};
      const seo = { ...(existing.seo || {}), [field]: valueToSet };
      return {
        ...prev,
        translations: {
          ...(prev.translations || {}),
          [activeLocale]: {
            ...existing,
            seo,
          },
        },
      };
    });
  };

  const handleSave = () => {
    const normalized = normalizeCategory(draft);
    const derivedId = normalized.id || slugify(normalized.title) || `category-${Date.now()}`;
    const payload = {
      ...normalized,
      id: slugify(derivedId),
      parentId: normalized.parentId ? slugify(normalized.parentId) : '',
      seo: {
        title: normalized.seo?.title || '',
        description: normalized.seo?.description || '',
        image: normalized.seo?.image || '',
      },
    };
    const trimmed = pruneTranslations(payload.translations);
    if (trimmed) payload.translations = trimmed;
    else delete payload.translations;

    if (!payload.title) {
      alert(t('title_required') || 'Title is required.');
      return;
    }

    onSave?.(payload);
  };

  const handleParentCommit = () => {
    const trimmed = String(parentInput || '').trim();
    if (!trimmed) {
      setDraft((prev) => ({ ...prev, parentId: '' }));
      setParentInput('');
      return;
    }
    setDraft((prev) => ({ ...prev, parentId: slugify(trimmed) }));
  };

  const selectedParent = useMemo(() => {
    if (!draft.parentId) return null;
    return parentOptions.find((cat) => String(cat.id) === String(draft.parentId)) || null;
  }, [draft.parentId, parentOptions]);

  const parentLabel = (option) => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.title || option.id || '';
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ fontWeight: 900 }}>
        {t('category') || 'Category'}
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#f8fafc' }}>
        <Stack spacing={2}>
          <Tabs
            value={activeLocale}
            onChange={(_, valueToSet) => setActiveLocale(valueToSet)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {languageTabs.map((lang) => (
              <Tab key={lang.code} value={lang.code} label={lang.label || lang.code} />
            ))}
          </Tabs>

          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <SectionNav sections={sections} onJump={handleJump} />
            </Grid>

            <Grid item xs={12} md={9} sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Box ref={sectionRefs.basics} />
                <Accordion
                  expanded={expanded.basics}
                  onChange={(_, nextExpanded) => setExpanded((prev) => ({ ...prev, basics: nextExpanded }))}
                >
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                    <Typography sx={{ fontWeight: 900 }}>{t('basics') || 'Basics'}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t('id') || 'ID'}
                          value={draft.id || ''}
                          onChange={(e) => setDraft((prev) => ({ ...prev, id: slugify(e.target.value) }))}
                          helperText={t('id_help') || 'Used in URLs. Leave empty to auto-generate from the title.'}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          freeSolo
                          options={parentOptions}
                          value={selectedParent}
                          inputValue={parentInput}
                          onInputChange={(_, next) => setParentInput(next)}
                          getOptionLabel={parentLabel}
                          onChange={(_, nextValue) => {
                            if (!nextValue) {
                              setParentInput('');
                              setDraft((prev) => ({ ...prev, parentId: '' }));
                              return;
                            }
                            if (typeof nextValue === 'string') {
                              setParentInput(nextValue);
                              setDraft((prev) => ({ ...prev, parentId: slugify(nextValue) }));
                              return;
                            }
                            setParentInput(nextValue.title || nextValue.id || '');
                            setDraft((prev) => ({ ...prev, parentId: nextValue.id || '' }));
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              label={t('parent_category') || 'Parent category'}
                              onBlur={handleParentCommit}
                              helperText={t('parent_category_help') || 'Optional. Used for grouping.'}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t('title') || 'Title'}
                          value={getLocalizedValue('title')}
                          onChange={(e) => setLocalizedValue('title', e.target.value)}
                        />
                      </Grid>
                    <Grid item xs={12}>
                      <RichTextEditor
                        label={t('description') || 'Description'}
                        value={getLocalizedValue('description')}
                        onChange={(valueToSet) => setLocalizedValue('description', valueToSet)}
                        placeholder={t('description') || 'Description'}
                        minHeight={180}
                      />
                    </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                <Box ref={sectionRefs.media} />
                <Accordion
                  expanded={expanded.media}
                  onChange={(_, nextExpanded) => setExpanded((prev) => ({ ...prev, media: nextExpanded }))}
                >
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                    <Typography sx={{ fontWeight: 900 }}>{t('media') || 'Media'}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <ImageUrlField
                        label={t('image_url') || 'Image URL'}
                        value={draft.image}
                        onChange={(valueToSet) => setDraft((prev) => ({ ...prev, image: valueToSet }))}
                        uploadPathPrefix={uploadPathPrefix}
                        helperText={t('category_image_help') || 'Displayed on category cards (optional).'}
                      />
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                <Box ref={sectionRefs.seo} />
                <Accordion
                  expanded={expanded.seo}
                  onChange={(_, nextExpanded) => setExpanded((prev) => ({ ...prev, seo: nextExpanded }))}
                >
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                    <Typography sx={{ fontWeight: 900 }}>{t('seo') || 'SEO'}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label={t('seo_title') || 'SEO title'}
                        value={getLocalizedSeoValue('title')}
                        onChange={(e) => setLocalizedSeoValue('title', e.target.value)}
                      />
                      <TextField
                        fullWidth
                        label={t('seo_description') || 'SEO description'}
                        value={getLocalizedSeoValue('description')}
                        onChange={(e) => setLocalizedSeoValue('description', e.target.value)}
                        multiline
                        minRows={2}
                      />
                      <ImageUrlField
                        label={t('og_image') || 'OpenGraph image'}
                        value={draft.seo?.image || ''}
                        onChange={(valueToSet) =>
                          setDraft((prev) => ({ ...prev, seo: { ...(prev.seo || {}), image: valueToSet } }))
                        }
                        uploadPathPrefix={uploadPathPrefix}
                      />
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                <Box ref={sectionRefs.publishing} />
                <Accordion
                  expanded={expanded.publishing}
                  onChange={(_, nextExpanded) => setExpanded((prev) => ({ ...prev, publishing: nextExpanded }))}
                >
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                    <Typography sx={{ fontWeight: 900 }}>{t('publishing') || 'Publishing'}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={1.5}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={draft.published !== false}
                            onChange={(_, checked) => setDraft((prev) => ({ ...prev, published: checked }))}
                          />
                        }
                        label={t('published') || 'Published'}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(draft.featured)}
                            onChange={(_, checked) => setDraft((prev) => ({ ...prev, featured: checked }))}
                          />
                        }
                        label={t('featured') || 'Featured'}
                      />
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 800 }}>
          {t('cancel') || 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{ textTransform: 'none', fontWeight: 900, borderRadius: 2 }}
          disabled={!draft.title}
        >
          {t('save') || 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
